import ScenarioConfigContext from "Context/ScenarioConfigContext";
import { useContext, useEffect, useReducer, useRef, useState } from "react";
import { 
  Box, 
  TextField, 
  Typography, 
  CircularProgress, 
  Grid, 
  Button,
  InputAdornment,
  FormControl,
  MenuItem,
  Select as MuiSelect
} from '@mui/material';
import TimelineIcon from '@mui/icons-material/Timeline';
import ElectricCarIcon from '@mui/icons-material/ElectricCar';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import DashboardCard from 'components/dashboard/common/DashboardCard';
import { isReadOnlyRole } from "utils/roleUtils";
import UserContext from "../../../../Context/UserProvider";
import { useApiService } from "../../../../services/ApiService/ApiService";
import toast from 'react-hot-toast';

// Add global CSS to remove spinner arrows
const globalStyles = `
  input[type="number"]::-webkit-outer-spin-button,
  input[type="number"]::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  input[type="number"] {
    -moz-appearance: textfield;
  }
`;

// Inject the styles
if (typeof document !== 'undefined') {
  const styleElement = document.createElement('style');
  styleElement.textContent = globalStyles;
  document.head.appendChild(styleElement);
}

const initialState = {
    businessHomeSinglePhaseCost: 0,
    businessDwellingDcCost: 0,
    businessDwellingThreePhaseCost: 0,
    businessDwellingSinglePhaseCost: 0,
    publicSinglePhaseCost: 0,
    publicThreePhaseCost: 0,
    publicDcCost: 0,
    evCombinedRangeFactor: 0,
    virtualChargingHubDcCost: 0,
    virtualChargingHubSinglePhaseCost: 0,
    virtualChargingHubThreePhaseCost: 0,
    pricePerLitrePetrol: 0,
    pricePerLitreDiesel: 0,
    operationalCostPerHourInGbp: 0,
    comparisonType: "",
};

function reducer(state: any, action: any) {
    switch (action.input) {
        case "businessHomeSinglePhaseCost":
            return { ...state, businessHomeSinglePhaseCost: +action.value };
        case "businessDwellingDcCost":
            return { ...state, businessDwellingDcCost: +action.value };
        case "businessDwellingThreePhaseCost":
            return { ...state, businessDwellingThreePhaseCost: +action.value };
        case "businessDwellingSinglePhaseCost":
            return { ...state, businessDwellingSinglePhaseCost: +action.value };
        case "publicDcCost":
            return { ...state, publicDcCost: +action.value };
        case "publicThreePhaseCost":
            return { ...state, publicThreePhaseCost: +action.value };
        case "publicSinglePhaseCost":
            return { ...state, publicSinglePhaseCost: +action.value };
        case "virtualChargingHubDcCost":
            return { ...state, virtualChargingHubDcCost: +action.value };
        case "virtualChargingHubThreePhaseCost":
            return { ...state, virtualChargingHubThreePhaseCost: +action.value };
        case "virtualChargingHubSinglePhaseCost":
            return { ...state, virtualChargingHubSinglePhaseCost: +action.value };
        case "evCombinedRangeFactor":
            return { ...state, evCombinedRangeFactor: +action.value };
        case "operationalCostPerHourInGbp":
            return { ...state, operationalCostPerHourInGbp: +action.value };
        case "pricePerLitrePetrol":
            return { ...state, pricePerLitrePetrol: +action.value };
        case "pricePerLitreDiesel":
            return { ...state, pricePerLitreDiesel: +action.value };
        case "comparisonType":
            return { ...state, comparisonType: action.value };
        default:
            return state;
    }
}

function ConfigureTabScreen(props: any) {
    const {userInfo, setUserInfo} = useContext(UserContext);
    const [scenarioConfigInfo, setScenarioConfigInfo] = useContext(ScenarioConfigContext) as any;
    const fleetScenarioUuid = scenarioConfigInfo.draftScenario?.uuid;
    const [state, dispatch] = useReducer(reducer, initialState);
    const [isError, setIsError] = useState(false);
    const currentInput = useRef() as any;
    const [isDisabled, setIsDisabled] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const { fetchWithAuth } = useApiService();
    const reference = [
        { label: "None", value: "INFRASTRUCTURE_NONE", name: "comparisonType" },
        { label: "None - No Home Recharge", value: "INFRASTRUCTURE_NONE_NO_HOME_RECHARGE", name: "comparisonType" },
        { label: "Actual", value: "INFRASTRUCTURE_ACTUAL", name: "comparisonType" },
    ];

    function onChange(e: any) {
        let action: any;
        if (e.target) {
            action = {
                input: e.target.name,
                value: e.target.value,
            };
        } else {
            action = {
                input: e.name,
                value: e.value,
            };
        }
        dispatch(action);
    }

    // Helper function to handle decimal input
    const handleDecimalChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        // Allow empty string, numbers, and decimals
        if (value === '' || /^\d*\.?\d*$/.test(value)) {
            onChange(e);
        }
    };

    async function fetchParameters() {
        setIsLoading(true);
        console.log("fleetScenarioUuid", fleetScenarioUuid);
        console.log("userInfo", userInfo);
        console.log("scenarioConfigInfo", scenarioConfigInfo);
        
        try {
            const response = await fetchWithAuth('/DragDrop/fleet/scenario/parameters/get', {
                method: 'POST',
                body: JSON.stringify({
                    uuid: userInfo.uuid,
                    fleetUuid: userInfo.fleetUuid,
                    fleetScenarioUuid: fleetScenarioUuid
                })
            });

            if (!response.ok) {
                throw new Error('Failed to fetch parameters');
            }

            const res_json = await response.json();

            dispatch({ input: "evCombinedRangeFactor", value: +res_json.evParameters.evCombinedRangeFactor * 100 });
            dispatch({ input: "publicDcCost", value: +res_json.evParameters.publicDcCost });
            dispatch({ input: "publicThreePhaseCost", value: +res_json.evParameters.publicThreePhaseCost });
            dispatch({ input: "publicSinglePhaseCost", value: +res_json.evParameters.publicSinglePhaseCost });
            dispatch({ input: "virtualChargingHubDcCost", value: +res_json.evParameters.virtualChargingHubDcCost });
            dispatch({ input: "virtualChargingHubThreePhaseCost", value: +res_json.evParameters.virtualChargingHubThreePhaseCost });
            dispatch({ input: "virtualChargingHubSinglePhaseCost", value: +res_json.evParameters.virtualChargingHubSinglePhaseCost });
            dispatch({ input: "businessDwellingDcCost", value: +res_json.evParameters.businessDwellingDcCost });
            dispatch({ input: "businessDwellingThreePhaseCost", value: +res_json.evParameters.businessDwellingThreePhaseCost });
            dispatch({ input: "businessDwellingSinglePhaseCost", value: +res_json.evParameters.businessDwellingSinglePhaseCost });
            dispatch({ input: "businessHomeSinglePhaseCost", value: +res_json.evParameters.businessHomeSinglePhaseCost });
            dispatch({ input: "operationalCostPerHourInGbp", value: +res_json.evParameters.operationalCostPerHourInGbp });
            dispatch({ input: "pricePerLitreDiesel", value: +res_json.iceParameters.pricePerLitreDiesel });
            dispatch({ input: "pricePerLitrePetrol", value: +res_json.iceParameters.pricePerLitrePetrol });
            dispatch({ input: "comparisonType", value: res_json.evParameters.comparisonType });
        } catch (error) {
            console.error('Error fetching parameters:', error);
            setIsError(true);
            toast.error('Failed to fetch parameters');
        } finally {
            setIsLoading(false);
        }
    }

    async function resetParameters() {
        await fetchParameters();
    }

    async function updateParameters() {
        setIsDisabled(true);
        try {
            const response = await fetchWithAuth('/DragDrop/fleet/scenario/parameters/update', {
                method: 'POST',
                body: JSON.stringify({
                    uuid: userInfo.uuid,
                    fleetUuid: userInfo.fleetUuid,
                    fleetScenarioUuid: fleetScenarioUuid,
                    evParameters: {
                        evCombinedRangeFactor: state.evCombinedRangeFactor / 100,
                        publicDcCost: state.publicDcCost,
                        publicThreePhaseCost: state.publicThreePhaseCost,
                        publicSinglePhaseCost: state.publicSinglePhaseCost,
                        virtualChargingHubDcCost: state.virtualChargingHubDcCost,
                        virtualChargingHubThreePhaseCost: state.virtualChargingHubThreePhaseCost,
                        virtualChargingHubSinglePhaseCost: state.virtualChargingHubSinglePhaseCost,
                        businessDwellingDcCost: state.businessDwellingDcCost,
                        businessDwellingThreePhaseCost: state.businessDwellingThreePhaseCost,
                        businessDwellingSinglePhaseCost: state.businessDwellingSinglePhaseCost,
                        businessHomeSinglePhaseCost: state.businessHomeSinglePhaseCost,
                        operationalCostPerHourInGbp: state.operationalCostPerHourInGbp,
                        comparisonType: state.comparisonType,
                    },
                    iceParameters: {
                        pricePerLitrePetrol: state.pricePerLitrePetrol,
                        pricePerLitreDiesel: state.pricePerLitreDiesel,
                    }
                })
            });

            if (!response.ok) {
                throw new Error('Failed to update parameters');
            }

            toast.success('Parameters updated successfully');
        } catch (error) {
            console.error('Error updating parameters:', error);
            setIsError(true);
            toast.error('Failed to update parameters');
        } finally {
            setIsDisabled(false);
        }
    }

    useEffect(() => {
        fetchParameters();
    }, []);

    return (
        <>
            {isLoading ? (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                    <CircularProgress />
                </Box>
            ) : (
                <Box sx={{ width: '100%' }}>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6} xl={4}>
                            <DashboardCard
                                title="Scenario Parameters"
                                icon={<TimelineIcon />}
                            >
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <Typography>WLTP Factor:</Typography>
                                        <TextField
                                            value={state.evCombinedRangeFactor === 0 ? '' : state.evCombinedRangeFactor}
                                            type="number"
                                            size="small"
                                            sx={{ width: 120 }}
                                            name="evCombinedRangeFactor"
                                            inputProps={{
                                                step: "any",
                                                min: "0"
                                            }}
                                            InputProps={{
                                                endAdornment: <InputAdornment position="end">%</InputAdornment>,
                                            }}
                                            onChange={handleDecimalChange}
                                        />
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <Typography>Operational Cost per Hour:</Typography>
                                        <TextField
                                            value={state.operationalCostPerHourInGbp === 0 ? '' : state.operationalCostPerHourInGbp}
                                            type="number"
                                            size="small"
                                            sx={{ width: 120 }}
                                            name="operationalCostPerHourInGbp"
                                            inputProps={{
                                                step: "any",
                                                min: "0"
                                            }}
                                            InputProps={{
                                                startAdornment: <InputAdornment position="start">£</InputAdornment>,
                                            }}
                                            onChange={handleDecimalChange}
                                        />
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <Typography>Comparison Type:</Typography>
                                        <FormControl size="small" sx={{ width: 120 }}>
                                            <MuiSelect
                                                value={state.comparisonType}
                                                name="comparisonType"
                                                onChange={onChange}
                                            >
                                                {reference.map((option) => (
                                                    <MenuItem key={option.value} value={option.value}>
                                                        {option.label}
                                                    </MenuItem>
                                                ))}
                                            </MuiSelect>
                                        </FormControl>
                                    </Box>
                                </Box>
                            </DashboardCard>
                        </Grid>

                        <Grid item xs={12} md={6} xl={4}>
                            <DashboardCard
                                title="EV Charging Costs"
                                icon={<ElectricCarIcon />}
                            >
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <Typography>Business Home Single Phase:</Typography>
                                        <TextField
                                            value={state.businessHomeSinglePhaseCost === 0 ? '' : state.businessHomeSinglePhaseCost}
                                            type="number"
                                            size="small"
                                            sx={{ width: 120 }}
                                            name="businessHomeSinglePhaseCost"
                                            inputProps={{
                                                step: "any",
                                                min: "0"
                                            }}
                                            InputProps={{
                                                startAdornment: <InputAdornment position="start">£</InputAdornment>,
                                            }}
                                            onChange={handleDecimalChange}
                                        />
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <Typography>Business Dwelling DC:</Typography>
                                        <TextField
                                            value={state.businessDwellingDcCost === 0 ? '' : state.businessDwellingDcCost}
                                            type="number"
                                            size="small"
                                            sx={{ width: 120 }}
                                            name="businessDwellingDcCost"
                                            inputProps={{
                                                step: "any",
                                                min: "0"
                                            }}
                                            InputProps={{
                                                startAdornment: <InputAdornment position="start">£</InputAdornment>,
                                            }}
                                            onChange={handleDecimalChange}
                                        />
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <Typography>Business Dwelling Three Phase:</Typography>
                                        <TextField
                                            value={state.businessDwellingThreePhaseCost === 0 ? '' : state.businessDwellingThreePhaseCost}
                                            type="number"
                                            size="small"
                                            sx={{ width: 120 }}
                                            name="businessDwellingThreePhaseCost"
                                            inputProps={{
                                                step: "any",
                                                min: "0"
                                            }}
                                            InputProps={{
                                                startAdornment: <InputAdornment position="start">£</InputAdornment>,
                                            }}
                                            onChange={handleDecimalChange}
                                        />
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <Typography>Business Dwelling Single Phase:</Typography>
                                        <TextField
                                            value={state.businessDwellingSinglePhaseCost === 0 ? '' : state.businessDwellingSinglePhaseCost}
                                            type="number"
                                            size="small"
                                            sx={{ width: 120 }}
                                            name="businessDwellingSinglePhaseCost"
                                            inputProps={{
                                                step: "any",
                                                min: "0"
                                            }}
                                            InputProps={{
                                                startAdornment: <InputAdornment position="start">£</InputAdornment>,
                                            }}
                                            onChange={handleDecimalChange}
                                        />
                                    </Box>
                                </Box>
                            </DashboardCard>
                        </Grid>

                        <Grid item xs={12} md={6} xl={4}>
                            <DashboardCard
                                title="Public Charging Costs"
                                icon={<LocalGasStationIcon />}
                            >
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <Typography>Public DC:</Typography>
                                        <TextField
                                            value={state.publicDcCost === 0 ? '' : state.publicDcCost}
                                            type="number"
                                            size="small"
                                            sx={{ width: 120 }}
                                            name="publicDcCost"
                                            inputProps={{
                                                step: "any",
                                                min: "0"
                                            }}
                                            InputProps={{
                                                startAdornment: <InputAdornment position="start">£</InputAdornment>,
                                            }}
                                            onChange={handleDecimalChange}
                                        />
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <Typography>Public Three Phase:</Typography>
                                        <TextField
                                            value={state.publicThreePhaseCost === 0 ? '' : state.publicThreePhaseCost}
                                            type="number"
                                            size="small"
                                            sx={{ width: 120 }}
                                            name="publicThreePhaseCost"
                                            inputProps={{
                                                step: "any",
                                                min: "0"
                                            }}
                                            InputProps={{
                                                startAdornment: <InputAdornment position="start">£</InputAdornment>,
                                            }}
                                            onChange={handleDecimalChange}
                                        />
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <Typography>Public Single Phase:</Typography>
                                        <TextField
                                            value={state.publicSinglePhaseCost === 0 ? '' : state.publicSinglePhaseCost}
                                            type="number"
                                            size="small"
                                            sx={{ width: 120 }}
                                            name="publicSinglePhaseCost"
                                            inputProps={{
                                                step: "any",
                                                min: "0"
                                            }}
                                            InputProps={{
                                                startAdornment: <InputAdornment position="start">£</InputAdornment>,
                                            }}
                                            onChange={handleDecimalChange}
                                        />
                                    </Box>
                                </Box>
                            </DashboardCard>
                        </Grid>

                        <Grid item xs={12} md={6} xl={4}>
                            <DashboardCard
                                title="Virtual Hub Costs"
                                icon={<ElectricCarIcon />}
                            >
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <Typography>Virtual Hub DC:</Typography>
                                        <TextField
                                            value={state.virtualChargingHubDcCost === 0 ? '' : state.virtualChargingHubDcCost}
                                            type="number"
                                            size="small"
                                            sx={{ width: 120 }}
                                            name="virtualChargingHubDcCost"
                                            inputProps={{
                                                step: "any",
                                                min: "0"
                                            }}
                                            InputProps={{
                                                startAdornment: <InputAdornment position="start">£</InputAdornment>,
                                            }}
                                            onChange={handleDecimalChange}
                                        />
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <Typography>Virtual Hub Three Phase:</Typography>
                                        <TextField
                                            value={state.virtualChargingHubThreePhaseCost === 0 ? '' : state.virtualChargingHubThreePhaseCost}
                                            type="number"
                                            size="small"
                                            sx={{ width: 120 }}
                                            name="virtualChargingHubThreePhaseCost"
                                            inputProps={{
                                                step: "any",
                                                min: "0"
                                            }}
                                            InputProps={{
                                                startAdornment: <InputAdornment position="start">£</InputAdornment>,
                                            }}
                                            onChange={handleDecimalChange}
                                        />
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <Typography>Virtual Hub Single Phase:</Typography>
                                        <TextField
                                            value={state.virtualChargingHubSinglePhaseCost === 0 ? '' : state.virtualChargingHubSinglePhaseCost}
                                            type="number"
                                            size="small"
                                            sx={{ width: 120 }}
                                            name="virtualChargingHubSinglePhaseCost"
                                            inputProps={{
                                                step: "any",
                                                min: "0"
                                            }}
                                            InputProps={{
                                                startAdornment: <InputAdornment position="start">£</InputAdornment>,
                                            }}
                                            onChange={handleDecimalChange}
                                        />
                                    </Box>
                                </Box>
                            </DashboardCard>
                        </Grid>

                        <Grid item xs={12} md={6} xl={4}>
                            <DashboardCard
                                title="Fuel Costs"
                                icon={<LocalGasStationIcon />}
                            >
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <Typography>Price per Litre (Petrol):</Typography>
                                        <TextField
                                            value={state.pricePerLitrePetrol === 0 ? '' : state.pricePerLitrePetrol}
                                            type="number"
                                            size="small"
                                            sx={{ width: 120 }}
                                            name="pricePerLitrePetrol"
                                            inputProps={{
                                                step: "any",
                                                min: "0"
                                            }}
                                            InputProps={{
                                                startAdornment: <InputAdornment position="start">£</InputAdornment>,
                                            }}
                                            onChange={handleDecimalChange}
                                        />
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <Typography>Price per Litre (Diesel):</Typography>
                                        <TextField
                                            value={state.pricePerLitreDiesel === 0 ? '' : state.pricePerLitreDiesel}
                                            type="number"
                                            size="small"
                                            sx={{ width: 120 }}
                                            name="pricePerLitreDiesel"
                                            inputProps={{
                                                step: "any",
                                                min: "0"
                                            }}
                                            InputProps={{
                                                startAdornment: <InputAdornment position="start">£</InputAdornment>,
                                            }}
                                            onChange={handleDecimalChange}
                                        />
                                    </Box>
                                </Box>
                            </DashboardCard>
                        </Grid>
                    </Grid>

                    <Box display="flex" justifyContent="center" mt={3} gap={2}>
                    <Button 
                            variant="outlined" 
                            onClick={resetParameters}
                            disabled={isDisabled}
                        >
                            Reset
                        </Button>
                        <Button 
                            variant="contained" 
                            onClick={updateParameters}
                            disabled={isDisabled}
                        >
                            Save Changes
                        </Button>
                    </Box>
                </Box>
            )}
        </>
    );
}

export default ConfigureTabScreen;
